import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../auth/AuthContext'
import { apiFetch } from '../api/client'
import type { DataHistoryResponse, DataRestoreResponse } from '../api/types'

// useDataHistory loads one page of per-key version history for the
// shared `data.*` surface of a glow. Re-fetches on key change. The
// query stays disabled until a key is submitted so an empty input
// doesn't fire requests on every render.
//
// v1 ships a single page (no infinite pagination). The server returns
// a next_cursor when more pages are available; we surface it to the
// caller via the response shape so the UI can decide later whether to
// add a "show older" button — for v1 the retention cron means free-
// tier glows almost never have more than 50 versions.
export function useDataHistory(glowID: string, key: string, enabled: boolean) {
  const { idTokenProvider, user } = useAuth()
  return useQuery({
    queryKey: ['data-history', glowID, key],
    queryFn: ({ signal }) =>
      apiFetch<DataHistoryResponse>(
        `/v1/glows/${glowID}/data-history?key=${encodeURIComponent(key)}&limit=100`,
        idTokenProvider,
        { signal },
      ),
    enabled: enabled && !!user && !!glowID && !!key,
  })
}

// useDataRestore POSTs the restore request and invalidates the
// matching history query plus the glow doc (data_bytes_used may have
// shifted from the restore size delta).
export function useDataRestore(glowID: string) {
  const { idTokenProvider } = useAuth()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { key: string; generation: number }) =>
      apiFetch<DataRestoreResponse>(
        `/v1/glows/${glowID}/data-restore`,
        idTokenProvider,
        { method: 'POST', body },
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['data-history', glowID, vars.key] })
      qc.invalidateQueries({ queryKey: ['glow', glowID] })
      // Phase 11.6 follow-up: the Data tab's directory listing depends
      // on the restored key's live state; invalidate the whole
      // ['data-keys', glowID] family so both the live view and any
      // open historical view re-fetch.
      qc.invalidateQueries({ queryKey: ['data-keys', glowID] })
      // The new live generation may add a fresh write event, which
      // belongs in the snapshot list; refresh that too.
      qc.invalidateQueries({ queryKey: ['data-snapshots', glowID] })
    },
  })
}
